import React from 'react';
import PropTypes from 'prop-types';
import AccessibilityInfo from "react-native-web/dist/exports/AccessibilityInfo";
import Platform from "react-native-web/dist/exports/Platform";
import { jsx as _jsx } from "react/jsx-runtime";
const ScreenReaderContext = /*#__PURE__*/React.createContext(false);
const ReducedMotionContext = /*#__PURE__*/React.createContext(false);

// eslint-disable-next-line no-unused-vars
const A11yInfoProvider = /*#__PURE__*/React.forwardRef((_ref, _) => {
  let {
    children
  } = _ref;
  const [reduceMotionEnabled, setReduceMotionEnabled] = React.useState(false);
  const [screenReaderEnabled, setScreenReaderEnabled] = React.useState(false);
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      // On Jest these effects do nothing but can cause `act` state change warnings
      // and "...after the Jest environment has been torn down" errors, so skip them.
      return () => {};
    }
    const motionSubscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotionEnabled);
    const setInitialA11yInfo = async () => {
      const [initialReduceMotionEnabled, initialScreenReaderEnabled] = await Promise.all([AccessibilityInfo.isReduceMotionEnabled(), AccessibilityInfo.isScreenReaderEnabled()]);

      // Browsers can't detect screen readers; in RNW isScreenReaderEnabled() is always `true`
      // https://github.com/necolas/react-native-web/blob/master/packages/react-native-web/src/exports/AccessibilityInfo/index.js#L14
      setScreenReaderEnabled(Platform.OS !== 'web' && !!initialScreenReaderEnabled);

      // RNW does support isReduceMotionEnabled looking for 'prefers-reduced-motion' state
      setReduceMotionEnabled(!!initialReduceMotionEnabled);
    };
    if (AccessibilityInfo.isReduceMotionEnabled && AccessibilityInfo.isScreenReaderEnabled) {
      setInitialA11yInfo();
    }
    return () => {
      // From react-native 0.65, AccessibilityInfo.removeEventListener is deprecated for `remove` on addEventListener return value
      if (typeof motionSubscription?.remove === 'function') {
        motionSubscription?.remove();
      } else if (typeof AccessibilityInfo.removeEventListener === 'function') {
        AccessibilityInfo.removeEventListener('reduceMotionChanged', setReduceMotionEnabled);
      }
    };
  }, []);
  return /*#__PURE__*/_jsx(ReducedMotionContext.Provider, {
    value: reduceMotionEnabled,
    children: /*#__PURE__*/_jsx(ScreenReaderContext.Provider, {
      value: screenReaderEnabled,
      children: children
    })
  });
});
A11yInfoProvider.displayName = 'A11yInfoProvider';
A11yInfoProvider.propTypes = {
  children: PropTypes.node.isRequired
};
export default A11yInfoProvider;
export const useA11yInfo = () => {
  const screenReaderEnabled = React.useContext(ScreenReaderContext);
  const reduceMotionEnabled = React.useContext(ReducedMotionContext);
  return {
    reduceMotionEnabled,
    screenReaderEnabled
  };
};